import { Formik } from 'formik';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { FormModal, GFormFields } from '@components';
import { applicationFormSchema, getCdnApplicationFormConfig } from '@constants';
import { getAllStorageData } from '@core/storage';
import { Dictionaries, FormType, originType } from '@enums';
import { useAppDispatch, useFormContent, useFormError } from '@hooks';
import { submitFormRequest } from '@store';
import i18n from '@translations';
import type { CDNData, DialogBase, FormConfig } from '@types';
import { getOptionListFromCatalog } from '@utils';

export const CdnApplicationModal: FC<DialogBase> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const formType = FormType.Cdn;
  const optionListName = Dictionaries.OriginType;

  const { current_workspace_id: workspace_id } = getAllStorageData();

  const currentCdnData = useFormContent({ formType });
  const submitError = useFormError({ formType });

  const applicationOptionList = useMemo(() => getOptionListFromCatalog({ name: optionListName }), [optionListName]);
  const schema = applicationFormSchema;

  const formConfig: FormConfig = useMemo(
    () =>
      getCdnApplicationFormConfig({
        applicationOptionList,
        schema,
      }),
    [applicationOptionList, schema, i18n.language],
  );

  const handleSubmit = (values: CDNData) => {
    const { domain, name, type, origin, verify_ssl_cert } = values;
    const formData = {
      domain,
      name,
      ...(origin && {
        type,
        origin,
        verify_ssl_cert: type === originType.HTTPS ? verify_ssl_cert : false,
      }),
      workspace_id: workspace_id,
    };
    dispatch(
      submitFormRequest({
        formType,
        formData,
        globalOptions: {
          returnResult: true,
          content: true,
        },
        callbackFunction: { refetch: () => navigateToApplication },
      }),
    );
  };

  const navigateToApplication = () => {
    currentCdnData?.id && navigate(`${currentCdnData.id}/configuration`);
  };

  const initialValues: CDNData = {
    name: '',
    domain: '',
    type: originType.HTTPS,
    origin: '',
    verify_ssl_cert: true,
  };

  const { inputList, validationSchema, label } = formConfig;

  return (
    <Formik<CDNData> initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ touched, errors }) => {
        return (
          <FormModal
            title={label?.title}
            description={label?.description}
            onClose={onClose}
            submitError={submitError}
            formType={formType}
            open={open}
            additionalReturnFunction={navigateToApplication}
            size="lg"
            confirmButton={{ children: label?.confirmButton }}
          >
            <GFormFields inputList={inputList} touched={touched} errors={errors} />
          </FormModal>
        );
      }}
    </Formik>
  );
};
